import React, { useEffect, useState } from 'react';
import { Button, Card, Input, Modal, Space, Switch, Table, TablePaginationConfig } from 'antd';
import {
  LockOutlined,
  UnlockOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
} from '@ant-design/icons';

import { User } from '@services/types';
import api from '@services/api';
import { toast } from 'react-toastify';
import styles from './users-table.module.scss';
import { AppContext } from '@layouts/AppRouter';
import { getUserRoleByRoles, isUserLocked } from '@services/utils';
import cn from 'classnames';
import { useHistory } from 'react-router-dom';
import { Navigation } from '@constants/Navigation';
import { SorterResult } from 'antd/es/table/interface';
import { Role, UsersResponse } from '@openapi';

const { Search } = Input;

const { Column } = Table;

interface SorterState {
  columnKey?: string;
  order?: 'ascend' | 'descend' | undefined;
}

const AdminUsers = () => {
  const [users, setUsers] = React.useState<User[]>([]);
  const { user, variablesProfile } = React.useContext(AppContext);
  const isSaml = variablesProfile?.profile === 'saml';
  const history = useHistory();
  const [total, setTotal] = useState<UsersResponse['totalElementCount']>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [searchResult, setSearchResult] = useState('');
  const [sortOrder, setSortOrder] = useState<SorterState>({});
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    total: users.length,
  });

  const getUsers = (newPagination = pagination, sorter = sortOrder) => {
    const params: any = {
      limit: newPagination.pageSize,
      offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
    };
    if (sorter?.order) {
      params.direction = sorter?.order === 'ascend' ? 'ASC' : 'DESC';
      params.orderBy = sorter?.columnKey;
    }
    api.administration
      .getUsers({
        ...params,
        filter: searchResult,
      })
      .then((data) => {
        setUsers(data.users);
        setTotal(data.totalCount);
        setPagination((prevState: any) => ({
          ...prevState,
          total: data.totalCount,
        }));
      });
  };
  useEffect(() => {
    getUsers();
  }, []);

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig, sorter: any) => {
      setPagination(newPagination);
      setSortOrder(sorter);
      getUsers(newPagination, sorter);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResult],
  );

  const handleDelete = (user: User) => {
    Modal.confirm({
      title: 'Are you sure delete this user?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setIsLoading(true);
          await api.administration.deleteUser(user.id);
          toast.success('User was deleted successfully');
          getUsers();
        } catch (error) {
          setIsLoading(false);
          console.log('User was not deleted ~ error', error);
          toast.error('User was not deleted');
        } finally {
          setIsLoading(false);
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const showLockConfirm = (user: User) => {
    Modal.confirm({
      title: isUserLocked(user) ? 'Are you sure unlock this user?' : 'Are you sure lock this user?',
      icon: <ExclamationCircleOutlined />,
      okText: isUserLocked(user) ? 'Unlock' : 'Lock',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          if (isUserLocked(user)) {
            await api.administration.unlockUser(user.id);
          } else {
            await api.administration.lockUser(user.id);
          }
          api.administration.getUsers().then((data) => {
            setUsers(data.users);
          });
          toast.success(isUserLocked(user) ? 'User was unlocked successfully' : 'User was locked successfully');
        } catch (error) {
          console.log('User was not locked ~ error', error);
          toast.error('User was not locked');
        }
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  };

  const handleMfaChange = async (val: boolean, userData: User) => {
    try {
      await api.administration.updateUser(userData.id, {
        ...userData,
        mfaEnabled: val,
        role: getUserRoleByRoles(userData.roles) ?? Role.Superadmin,
        superGroupIds: userData?.etc?.perimeter || [],
      });
      toast.dismiss();
      toast.success('MFA was updated successfully');
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const apiCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const apiItem = (apiCount || 0) - ((pagination.pageSize || 0) - 1);

  return (
    <Card
      title={`Users: ${apiItem}-${apiCount} / ${total}`}
      extra={
        <Space>
          <Search
            onChange={(e) => {
              setSearchResult(e.target.value);
            }}
            placeholder="Search by email"
            allowClear
            enterButton
            size="middle"
            onSearch={() => getUsers()}
          />
          <Button type="primary" onClick={() => history.push(Navigation.CreateUser)}>
            Add
          </Button>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <Table
        dataSource={users}
        pagination={pagination}
        loading={isLoading}
        onChange={(paginationArg, filters, sorterArg) => {
          handleTableChange(paginationArg, sorterArg);
        }}
        onRow={(record: User) => ({
          className: cn(
            styles.unLock,
            record.isLocked && styles.lock,
            record.isAuthenticationLocked && styles.authLock,
          ),
        })}
        rowKey={(record: User) => record.id}
        scroll={{ x: 1200 }}
      >
        <Column title="Email" dataIndex="email" key="email" sorter={true} sortDirections={['ascend', 'descend']} />
        <Column title="First Name" dataIndex="firstName" key="firstName" />
        <Column title="Last Name" dataIndex="lastName" key="lastName" />
        <Column
          title="Last Activity"
          dataIndex="lastActivity"
          key="lastConnectionDate"
          sorter={true}
          sortDirections={['ascend', 'descend']}
        />
        <Column
          title="Role"
          align="center"
          dataIndex="roles"
          key="role"
          render={(roles: User['roles']) => getUserRoleByRoles(roles)}
          sorter={true}
          sortDirections={['ascend', 'descend']}
        />
        {!isSaml && (
          <Column
            title="MFA enabled"
            align="center"
            dataIndex="mfaEnabled"
            key="mfaEnabled"
            render={(mfaEnabled: boolean, userData: User) => {
              return (
                <Switch
                  disabled={user?.id === userData.id}
                  defaultChecked={mfaEnabled}
                  onChange={async (val) => {
                    await handleMfaChange(val, userData);
                  }}
                />
              );
            }}
          />
        )}
        <Column
          title="Scope"
          width="250px"
          align="center"
          dataIndex="etc.perimeter"
          key="scope"
          render={(_text, record: User) => record?.etc?.perimeterNames?.join(', ')}
          sorter={true}
          sortDirections={['ascend', 'descend']}
        />
        <Column
          width="130px"
          title="Actions"
          key="action"
          fixed="right"
          align="center"
          render={(_text, newUser: User) => (
            <Space size="middle">
              {user?.id === newUser.id ? (
                <InfoCircleOutlined onClick={() => history.push(Navigation.UserInfo.replace(':id', newUser.id))} />
              ) : (
                <Space size="middle">
                  <InfoCircleOutlined onClick={() => history.push(Navigation.UserInfo.replace(':id', newUser.id))} />
                  <FormOutlined onClick={() => history.push(Navigation.UpdateUser.replace(':id', newUser.id))} />
                  <DeleteOutlined onClick={() => handleDelete(newUser)} />
                  {isUserLocked(newUser) ? (
                    <UnlockOutlined onClick={() => showLockConfirm(newUser)} />
                  ) : (
                    <LockOutlined onClick={() => showLockConfirm(newUser)} />
                  )}
                </Space>
              )}
            </Space>
          )}
        />
      </Table>
    </Card>
  );
};

export default AdminUsers;
